import * as React from "react";
import { FC, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import ReactTable, { RowInfo } from "react-table";
import * as ReactTooltip from "react-tooltip";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { TruncatedCell } from "src/components/layouts";
import { TrialInstitutionsBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { TrialSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import * as TrialActions from "../../../actions/trial";
import * as TrialInstitutionActions from "../../../actions/trialInstitution";
import { FormFilterInput } from "../../../components/form";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    convertToApproximateDateString,
    convertToShortDateString,
} from "../../../helpers/date";
import { findLookupValue } from "../../../helpers/lookupHelper";
import { IFormFilter, ITmdState } from "../../../reducers/index";
import { TrialLayout } from "../../index";

import "./TrialInstitutionsPage.scss";

interface TrialInstitutionsPageProps {
    formFilter: IFormFilter;
    trial: Dtos.Trial;
    trialInstitutions: Dtos.TrialInstitution[];
    loadingTrial: boolean;
    loadTrialSuccess: boolean;
    loadTrialFailure: boolean;
    loadTrialErrorCode: string;

    loadingTrialInstitutions: boolean;
    loadTrialInstitutionsSuccess: boolean;
    loadTrialInstitutionsFailure: boolean;
    loadTrialInstitutionsErrorCode: string;

    permissions?: Dtos.PersonnelTmdPermissions[];
    lookups: Dtos.Lookup[];
    pageSize: number;
    clearTrial: TrialActions.ITrialClearActionCreator;
    loadTrial: TrialActions.ITrialLoadByIdActionCreator;
    clearTrialInstitutions: TrialInstitutionActions.ITrialInstitutionClearActionCreator;
    loadTrialInstitutions: TrialInstitutionActions.ITrialInstitutionsLoadByTrialIdActionCreator;
    filter: TrialInstitutionActions.ITrialInstitutionFilterActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
}

const TrialInstitutionsPage: FC<TrialInstitutionsPageProps> = ({
    formFilter,
    trial,
    trialInstitutions,
    loadingTrial,
    loadTrialSuccess,
    loadTrialFailure,
    loadTrialErrorCode,
    loadingTrialInstitutions,
    loadTrialInstitutionsSuccess,
    loadTrialInstitutionsFailure,
    loadTrialInstitutionsErrorCode,
    permissions,
    lookups,
    pageSize,
    clearTrial,
    loadTrial,
    clearTrialInstitutions,
    loadTrialInstitutions,
    filter,
    setBreadcrumbs,
    setDrawerItems,
    setPageSize
}) => {
    //
    // - Params --
    const { trialId } = useParams<{ trialId: string }>();
    const trialIdNumber = parseInt(trialId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- Variables --
    const showTrialInstitutions = !loadingTrial && loadTrialSuccess && !loadingTrialInstitutions && loadTrialInstitutionsSuccess;


    // Load trial
    useEffect(() => {
        if (trialIdNumber && !trial) {
            setDrawerItems(TrialSubMenu(trialIdNumber, permissions));
            setBreadcrumbs(TrialInstitutionsBreadcrumbs(trialIdNumber));

            loadTrial(trialIdNumber);
        }
    }, [
        trialIdNumber,
        trial,
        setDrawerItems,
        TrialSubMenu,
        permissions,
        setBreadcrumbs,
        TrialInstitutionsBreadcrumbs,
        loadTrial,
    ]);

    // Load trial institution
    useEffect(() => {
        if (trial && !loadingTrialInstitutions && !loadTrialInstitutionsSuccess) {
            loadTrialInstitutions(trial.id);
        }
    }, [
        trial,
        loadingTrialInstitutions,
        loadTrialInstitutionsSuccess
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialFailure) {
            navigate(`/error/${loadTrialErrorCode}`);
        }
        if (loadTrialInstitutionsFailure) {
            navigate(`/error/${loadTrialInstitutionsErrorCode}`);
        }
    }, [
        loadTrialFailure, 
        loadTrialErrorCode, 
        loadTrialInstitutionsFailure, 
        loadTrialInstitutionsErrorCode, 
        navigate
    ]);

    // Cleanup effect to clear the trial on component unmount
    useEffect(() => {
        return () => {
            clearTrial();
            clearTrialInstitutions();
        };
    }, [clearTrial, clearTrialInstitutions]);

    // Update breadcrumbs
    useEffect(() => {
        if (trialIdNumber && trial?.displayName) {
            setBreadcrumbs(
                TrialInstitutionsBreadcrumbs(trialIdNumber, trial.displayName)
            );
        }
    }, [trialIdNumber, trial, TrialInstitutionsBreadcrumbs, setBreadcrumbs]);

    const checkIfClosed = (closedDate: string): boolean => {
        return Date.now() > Date.parse(closedDate);
    };

    const createTable = useMemo(() => {
        if (!trialInstitutions || trialInstitutions.length === 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>There are currently no trial institutions</span>
                    </div>
                </div>
            );
        }

        const showPagination = trialInstitutions?.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={trialInstitutions}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination ? pageSize : trialInstitutions.length
                    }
                    pageSize={
                        showPagination ? pageSize : trialInstitutions.length
                    }
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no trial institutions to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (e, handleOriginal) => {
                                const url: string = `/trials/${rowInfo.original.trialId}/trialinstitution/${rowInfo.original.id}/details`;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    defaultSorted={[
                        {
                            id: "name",
                            desc: false,
                        },
                    ]}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (props) => (
                                <div className="large-small-icon">
                                    <FontAwesomeIcon
                                        className="large-icon"
                                        icon={FontAwesomeIcons.Duotone.TRIAL}
                                        fixedWidth
                                    />
                                    {props.original.parentInstitutionId &&
                                    props.original.parentInstitutionId > 0 ? (
                                        <FontAwesomeIcon
                                            className="medium-icon"
                                            icon={
                                                FontAwesomeIcons.Duotone
                                                    .INSTITUTION
                                            }
                                            fixedWidth
                                        />
                                    ) : (
                                        <></>
                                    )}
                                    <FontAwesomeIcon
                                        className="small-icon"
                                        icon={
                                            FontAwesomeIcons.Duotone.INSTITUTION
                                        }
                                        fixedWidth
                                    />
                                </div>
                            ),
                        },
                        {
                            id: "name",
                            Header: "Institution",
                            accessor: "institution.name",
                            Cell: (props) =>
                                props.original.parentInstitutionId &&
                                props.original.parentInstitutionId > 0 ? (
                                    <span>
                                        {props.value}&nbsp;
                                        <small className="text-muted">
                                            via&nbsp;
                                            <i className="text-muted">
                                                {
                                                    props.original
                                                        .parentInstitution.name
                                                }
                                            </i>
                                        </small>
                                    </span>
                                ) : (
                                    <TruncatedCell value={props.value} />
                                ),
                        },
                        {
                            id: "trialInstitutionStatusId",
                            Header: "Status",
                            accessor: "trialInstitutionStatusId",
                            maxWidth: 250,
                            minWidth: 180,
                            Cell: (props) => (
                                <div>
                                    <span
                                        data-tip={convertToApproximateDateString(
                                            props.value
                                        )}
                                    >
                                        {findLookupValue(
                                            "TrialInstitution",
                                            "TrialInstitutionStatusId",
                                            props.value,
                                            lookups
                                        )}
                                    </span>
                                    <ReactTooltip />
                                </div>
                            ),
                        },
                        {
                            id: "dateActivated",
                            Header: "Date Activated",
                            accessor: "dateActivated",
                            maxWidth: 180,
                            Cell: (props) => (
                                <div>
                                    <span
                                        data-tip={convertToApproximateDateString(
                                            props.value
                                        )}
                                    >
                                        {convertToShortDateString(props.value)}
                                    </span>
                                    <ReactTooltip />
                                </div>
                            ),
                        },
                        {
                            id: "closureDate",
                            Header: "Closed",
                            accessor: "closureDate",
                            maxWidth: 80,
                            Cell: (props) => (
                                <div>
                                    {checkIfClosed(props.value) ? (
                                        <FontAwesomeIcon
                                            className="large-icon"
                                            icon={
                                                FontAwesomeIcons.Duotone
                                                    .SQUARECHECK
                                            }
                                        />
                                    ) : null}
                                </div>
                            ),
                        },
                        {
                            id: "institutionCodeId",
                            Header: "Code",
                            accessor: "",
                            maxWidth: 100,
                            Cell: (props) => (
                                <div>
                                    <span>
                                        {
                                            lookups
                                                .find(
                                                    (x) =>
                                                        x.type ==
                                                            "TrialInstitution" &&
                                                        x.propertyName ==
                                                            "InstitutionCodeId"
                                                )
                                                ?.values.find(
                                                    (x) =>
                                                        x.id ==
                                                        props?.original
                                                            ?.institutionCodeId
                                                )?.value
                                        }
                                    </span>
                                </div>
                            ),
                        },
                        {
                            id: "groupId",
                            Header: "Group",
                            accessor: "",
                            Cell: (props) => (
                                <TruncatedCell
                                    value={findLookupValue(
                                        "TrialInstitution",
                                        "GroupId",
                                        props.original.groupId,
                                        lookups
                                    )}
                                />
                            ),
                        },
                    ]}
                />
            </div>
        );
    }, [trialInstitutions, PAGE_SIZE_OPTIONS, pageSize, setPageSize, navigate]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-md-12 col-lg-auto mb-2"}>
                        <h1>Trial Institutions</h1>
                    </div>
                    <div
                        className="col-md-12 col-lg d-flex justify-content-md-start justify-content-lg-end align-items-stretch mb-md-2 mb-lg-0"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.TrialInstitutionCreate,
                            trialIdNumber,
                            null
                        ) &&
                            trial &&
                            trial.id > 0 && (
                                <Link
                                    className={"btn btn-primary"}
                                    url={`/trials/${trial?.id}/trialinstitution/create/details`}
                                >
                                    Add Institution
                                </Link>
                            )}
                    </div>
                </div>
                {!showTrialInstitutions ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showTrialInstitutions ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [formFilter, filter, permissions, showTrialInstitutions, createTable]);

    return (
        <TrialLayout
            loading={loadingTrial || loadingTrialInstitutions}
            permission={Dtos.Permission.TrialInstitutionView}
        >
            {renderContent}
        </TrialLayout>
    );
};

const trialInstitutionActiveFilter = (
    items: Dtos.TrialInstitution[],
    filter: IFormFilter
): Dtos.TrialInstitution[] => {
    if (items && filter) {
        if (!filter.showInActive) {
            items = items.filter(
                (f) => f.active && f.institution && f.institution.active
            );
        }
        if (filter.search.length > 0) {
            items = items.filter(
                (f) =>
                    f.institutionDisplay
                        .toLowerCase()
                        .indexOf(filter.search.toLowerCase()) > -1
            );
        }
        return items;
    }

    return [];
};

const mapStateToProps = (
    state: ITmdState
): Partial<TrialInstitutionsPageProps> => {
    let trial: Dtos.Trial | undefined = !(
        state.trials.formData instanceof Array
    )
        ? state.trials.formData
        : undefined;
    let trialInstitutions: Dtos.TrialInstitution[] | undefined = (state.trialInstitution.formData instanceof Array)
        ? state.trialInstitution.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        formFilter: state.trialInstitution.formFilter,
        trial: trial!,
        trialInstitutions: trialInstitutionActiveFilter(
            trialInstitutions!,
            state.trialInstitution.formFilter!
        ),
        lookups: state.trials.lookups,
        loadingTrial:
            state.trials.loadState &&
            state.trials.loadState.status == RequestState.Pending,
        loadTrialSuccess:
            state.trials.loadState &&
            state.trials.loadState.status == RequestState.Success,
        loadTrialFailure:
            state.trials.loadState &&
            state.trials.loadState.status == RequestState.Failure,
        loadTrialErrorCode: state.trials.loadState.errorCode,
        loadingTrialInstitutions:
            state.trialInstitution.loadState &&
            state.trialInstitution.loadState.status == RequestState.Pending,
        loadTrialInstitutionsSuccess:
            state.trialInstitution.loadState &&
            state.trialInstitution.loadState.status == RequestState.Success,
        loadTrialInstitutionsFailure:
            state.trialInstitution.loadState &&
            state.trialInstitution.loadState.status == RequestState.Failure,
        loadTrialInstitutionsErrorCode: state.trialInstitution.loadState.errorCode,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
    };
};

const mapDispatchToProps = (dispatch): Partial<TrialInstitutionsPageProps> => {
    return {
        filter: bindActionCreators(TrialInstitutionActions.Filter, dispatch),

        loadTrial: bindActionCreators(TrialActions.LoadTrialById, dispatch),
        clearTrial: bindActionCreators(TrialActions.Clear, dispatch),

        loadTrialInstitutions: bindActionCreators(TrialInstitutionActions.LoadTrialInstitutionsByTrialId, dispatch),
        clearTrialInstitutions: bindActionCreators(TrialInstitutionActions.Clear, dispatch),

        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        ),
        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TrialInstitutionsPage);
